import axios from "./_axios";
import { API } from "./apiEndPoints";
import { buildFetchConfig, processSSEStream } from "./askIQApi";
import configService from "@/services/config";
import {
  toDraftApiRequest,
  toSuggestApiRequest,
} from "@components/PayRangeModal/payRangeDraftPayload";

/**
 * Pay Range APIs — list (Rule 18) + draft/lifecycle (ER-10460 v2).
 */

export const payRangeApiConfig = {
  useMock: false,
};

/**
 * Account context every pay-range request is scoped to.
 *
 * Read at call time, so a caller holding data it already loaded must compare
 * this against the account that load used before firing a write (ER-11664).
 */
export function getAccountId() {
  try {
    return JSON.parse(localStorage.getItem("currentAccountID"));
  } catch {
    return localStorage.getItem("currentAccountID");
  }
}

function delay(ms = 400) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function accountPayRangeBase(accountId = getAccountId()) {
  return `${API.ACCOUNT_LIST_V2}${accountId}/pay-range/`;
}

/**
 * Body shape expected by create-draft / edit-draft (v2).
 * ER-11729 §8: carries job_family_level_id + structured `location` instead of the
 * legacy `name` / `operating_country_id` / `operating_region` / `is_country_wide`.
 */
function toV2DraftBody(form) {
  return toDraftApiRequest(form);
}

function isBrowserFile(value) {
  if (!value || typeof value !== "object") return false;
  if (typeof File !== "undefined" && value instanceof File) return true;
  // Duck-type File/Blob for environments where instanceof File can fail.
  return (
    typeof value.name === "string" &&
    typeof value.size === "number" &&
    typeof value.slice === "function"
  );
}

function getSourceDocumentFile(form) {
  if (isBrowserFile(form?.sourceDocumentFile)) {
    return form.sourceDocumentFile;
  }
  if (isBrowserFile(form?.sourceDocument)) {
    return form.sourceDocument;
  }
  return null;
}

/**
 * JSON when no PDF; multipart FormData when a source document is attached
 * so create/edit-draft can persist fields + file in one request.
 *
 * Must override the shared axios default Content-Type (application/json);
 * otherwise the File is JSON-serialized to `{}` and arrives null on the API.
 */
function toV2DraftRequestPayload(form) {
  const body = toV2DraftBody(form);
  const file = getSourceDocumentFile(form);
  if (!file) {
    return { data: body };
  }

  const formData = new FormData();
  Object.entries(body).forEach(([key, value]) => {
    if (value == null || value === "") return;
    // Nested objects (e.g. `location`) are JSON-encoded so multipart keeps structure.
    formData.append(key, typeof value === "object" ? JSON.stringify(value) : String(value));
  });
  formData.append("source_document", file, file.name || "document.pdf");
  return {
    data: formData,
    headers: { "Content-Type": "multipart/form-data" },
  };
}

/**
 * List pay ranges (Rule 18 filter surface).
 * @param {{
 *   page?: number,
 *   pageSize?: number,
 *   status?: string[],
 *   operating_country?: string|number,
 *   operating_currency?: string|number,
 *   pay_basis?: string,
 *   work_mode?: string,
 *   code?: string Pay range code (exact match) — ER-12098; the retired `name` substring filter
 *           was removed by ER-11730, which re-keyed the list to `code` + `job_family_level`.
 *   search?: string Free-text lookup: substring over the code and the live Job Family Level
 *           label (level / family name). ER-12098 follow-up for the subscribe picker.
 *   as_of?: string Local date (YYYY-MM-DD) the list resolves versions against — ER-12150.
 * }} [params]
 */
export async function listPayRanges(params = {}) {
  const accountId = getAccountId();
  const query = new URLSearchParams();

  query.set("page", String(params.page || 1));
  query.set("page_size", String(params.pageSize || 10));
  if (params.operating_country) {
    query.set("operating_country", String(params.operating_country));
  }
  if (params.operating_currency) {
    query.set("operating_currency", String(params.operating_currency));
  }
  if (params.pay_basis) query.set("pay_basis", String(params.pay_basis));
  if (params.work_mode) query.set("work_mode", String(params.work_mode));
  if (params.code) query.set("code", String(params.code));
  if (params.search) query.set("search", String(params.search));
  if (params.as_of) query.set("as_of", String(params.as_of));
  (params.status || []).forEach((s) => query.append("status", s));

  const qs = query.toString();
  return axios()({
    method: "get",
    url: `${accountPayRangeBase(accountId)}${qs ? `?${qs}` : ""}`,
  });
}

/**
 * Create a DRAFT pay_range from wizard form payload.
 * Sends all draft fields (and optional PDF) in one create-draft request.
 * @param {import('@components/PayRangeModal/payRangeDraftPayload').PayRangeDraftFormPayload} form
 */
export async function createPayRangeDraft(form) {
  const accountId = getAccountId();
  const { data, headers } = toV2DraftRequestPayload(form);

  if (payRangeApiConfig.useMock) {
    await delay();
    const body = toV2DraftBody(form);
    return {
      data: {
        id: `draft-${Date.now()}`,
        ...body,
        status: "DRAFT",
      },
    };
  }

  return axios()({
    method: "post",
    url: `${accountPayRangeBase(accountId)}create-draft/`,
    data,
    ...(headers ? { headers } : {}),
  });
}

/**
 * Update an existing DRAFT pay_range (Rule 19 — scope still mutable).
 * @param {string|number} payRangeId
 * @param {import('@components/PayRangeModal/payRangeDraftPayload').PayRangeDraftFormPayload} form
 */
export async function updatePayRangeDraft(payRangeId, form) {
  const accountId = getAccountId();
  const { data, headers } = toV2DraftRequestPayload(form);

  if (payRangeApiConfig.useMock) {
    await delay();
    const body = toV2DraftBody(form);
    return {
      data: {
        id: payRangeId,
        ...body,
        status: "DRAFT",
      },
    };
  }

  return axios()({
    method: "post",
    url: `${accountPayRangeBase(accountId)}${payRangeId}/edit-draft/`,
    data,
    ...(headers ? { headers } : {}),
  });
}

/**
 * @deprecated Prefer attaching the PDF on create/edit-draft (multipart).
 * There is no standalone source-document endpoint on PayRangeV2.
 * Kept for pinia/store callers; resolves locally without a network call.
 * @param {File} file
 * @param {{ payRangeId?: string|null }} [options]
 */
export async function uploadPayRangeSourceDocument(file, options = {}) {
  await delay(100);
  return {
    data: {
      id: options.payRangeId ? `pending-${options.payRangeId}` : `pending-${Date.now()}`,
      name: file.name,
      size: file.size,
      type: file.type || "application/pdf",
      deferred: true,
    },
  };
}

/**
 * Save draft from wizard — create or edit based on form.mode.
 * Includes sourceDocumentFile when present (single multipart request).
 */
export async function savePayRangeDraft(form) {
  if (form.mode === "edit" && form.payRangeId) {
    return updatePayRangeDraft(form.payRangeId, form);
  }
  return createPayRangeDraft(form);
}

/** Normalize a wizard form payload or a pre-built suggest API body. */
function normalizeSuggestRequestBody(seed) {
  if (!seed || typeof seed !== "object") return {};
  if (seed.job_family_level_id != null && seed.scope_level) return seed;
  if (seed.location || seed.jobFamily || seed.jobFamilyLevelId != null) {
    return toSuggestApiRequest(seed);
  }
  return seed;
}

/** Shape a fetch HTTP failure like an axios error so Rule 13 mapping is unchanged. */
async function toSuggestHttpError(response) {
  let data = null;
  try {
    data = await response.json();
  } catch {
    data = null;
  }
  const detail = data && typeof data === "object" ? data.detail : null;
  const error = new Error(detail || `Suggest Range failed (${response.status}).`);
  error.response = { status: response.status, data: data || {} };
  return error;
}

/** Shape a terminal SSE `error` frame, preserving the machine-readable reason. */
function toSuggestStreamError(data, fallbackMessage) {
  const reason = data && typeof data.reason === "string" ? data.reason.trim() : "";
  const detail =
    data && typeof data.detail === "string" && data.detail.trim()
      ? data.detail.trim()
      : data && typeof data.message === "string" && data.message.trim()
      ? data.message.trim()
      : "";
  const error = new Error(detail || fallbackMessage || "Suggest Range failed.");
  if (reason) error.reason = reason;
  error.streamTerminal = true;
  return error;
}

/** Legacy non-streamed JSON body (endpoint still answering application/json). */
async function readSuggestJsonBody(response) {
  let parsed = null;
  try {
    parsed = JSON.parse(await response.text());
  } catch {
    parsed = null;
  }
  if (parsed && typeof parsed === "object" && parsed.error) {
    const error = new Error(String(parsed.detail || parsed.error || "Suggest Range failed."));
    error.response = { status: response.status, data: parsed };
    throw error;
  }
  if (!parsed || typeof parsed !== "object") {
    throw new Error("Suggest Range returned an unreadable response.");
  }
  return parsed;
}

/**
 * AI-assisted sourcing — stream market data for the draft context.
 *
 * POST .../pay-range/suggest/stream/ (ER-12219, Streamed AI Thinking Standard).
 * The SSE body carries the standard lifecycle:
 * - `event: phase`    → distilled step label (`onPhase`)
 * - `event: thinking` → accumulated narrative progress (`onThinking`)
 * - `event: stalled`  → silence watchdog (`onStalled`), stream stays open
 * - `event: result`   → terminal `{ sources, suggestion }` (resolves as `{ data }`)
 * - `event: error`    → terminal `{ reason, detail }` where reason is
 *   `no_sources` | `timeout` | `unavailable`; rejected with `error.reason` set.
 *
 * `signal` aborts the request (Try Again / closing the wizard). Untouched
 * `suggestPayRange(seed)` callers keep the old one-shot `{ data }` shape.
 *
 * @param {object} seed Wizard form payload or normalized suggest API body
 * @param {{
 *   onThinking?: (text: string) => void,
 *   onPhase?: (phase: string) => void,
 *   onStalled?: () => void,
 *   signal?: AbortSignal,
 * }} [options]
 * @returns {Promise<{ data: { sources: Array, suggestion: { min: number|null, median: number|null, max: number|null } } }>}
 */
export async function suggestPayRange(seed, { onThinking, onPhase, onStalled, signal } = {}) {
  const accountId = getAccountId();
  // Native fetch ignores axios's defaults.baseURL, so resolve against the API host.
  const baseUrl = (configService.get("baseURL") || "").replace(/\/$/, "");
  const url = `${baseUrl}/${accountPayRangeBase(accountId)}suggest/stream/`;
  const payload = normalizeSuggestRequestBody(seed);

  const response = await fetch(
    url,
    buildFetchConfig(url, payload, signal, "text/event-stream, application/json;q=0.9")
  );

  if (!response.ok) {
    throw await toSuggestHttpError(response);
  }

  const contentType = String(response.headers?.get?.("content-type") || "").toLowerCase();

  // Legacy plain-JSON answer — resolve with the same shape as the old call.
  if (!contentType.includes("text/event-stream") || !response.body?.getReader) {
    return { data: await readSuggestJsonBody(response) };
  }

  const reader = response.body.getReader();
  const { finalResponse, streamError, streamErrorData } = await processSSEStream(reader, {
    onPhase,
    onThinkingUpdate: onThinking,
    onStalled,
  });

  if (streamErrorData || streamError) {
    throw toSuggestStreamError(streamErrorData, streamError);
  }

  if (!finalResponse) {
    throw new Error("Suggest Range ended without a result.");
  }

  return { data: finalResponse };
}

/**
 * Retrieve a single pay range (list/detail serializer).
 * @param {string|number} payRangeId
 */
export async function getPayRange(payRangeId) {
  const accountId = getAccountId();
  return axios()({
    method: "get",
    url: `${accountPayRangeBase(accountId)}${payRangeId}/`,
  });
}

/**
 * Lifecycle actions on a pay range (publish, retire, reactivate, archive,
 * unarchive, delete-draft, …). ER-11729 removed the rename action — no code
 * path calls the rename endpoint anymore.
 * @param {string|number} payRangeId
 * @param {string} action - URL path segment (e.g. "delete-draft")
 * @param {object} [body]
 * @param {'post'|'delete'} [method]
 */
export async function payRangeLifecycleAction(payRangeId, action, body = {}, method = "post") {
  const accountId = getAccountId();
  return axios()({
    method,
    url: `${accountPayRangeBase(accountId)}${payRangeId}/${action}/`,
    ...(method === "post" ? { data: body } : {}),
  });
}

/**
 * Activate / publish a first DRAFT (ER-10460 publish).
 * @param {string|number} payRangeId
 * @param {{ effective_from: string, effective_to?: string|null }} body
 */
export async function publishPayRange(payRangeId, body) {
  return payRangeLifecycleAction(payRangeId, "publish", body);
}

/**
 * Stage a superseding version draft on a PUBLISHED pay range.
 * @param {string|number} payRangeId
 * @param {{
 *   min_amount?: number|string|null,
 *   max_amount?: number|string|null,
 *   effective_from?: string|null,
 *   sources?: string|null,
 * }} body
 */
export async function savePayRangeVersionDraft(payRangeId, body) {
  return payRangeLifecycleAction(payRangeId, "save-version-draft", body);
}

/** Discard a staged version draft. */
export async function discardPayRangeVersionDraft(payRangeId) {
  return payRangeLifecycleAction(payRangeId, "discard-version-draft");
}

/** Publish a staged version draft (supersede). */
export async function publishPayRangeVersionDraft(payRangeId) {
  return payRangeLifecycleAction(payRangeId, "publish-version-draft");
}

/**
 * Void a published pay range version (ER-11598).
 * POST .../pay-range/<pk>/versions/<version_id>/void/
 * @param {string|number} payRangeId
 * @param {string|number} versionId
 * @param {{ reason?: string|null }} [body]
 */
export async function voidPayRangeVersion(payRangeId, versionId, body = {}) {
  const accountId = getAccountId();
  return axios()({
    method: "post",
    url: `${accountPayRangeBase(accountId)}${payRangeId}/versions/${versionId}/void/`,
    data: body,
  });
}

/**
 * Correct a published version in place with new dates/amounts (ER-12600).
 * The response is the full pay range detail payload.
 * POST .../pay-range/<pk>/versions/<version_id>/correct/
 * @param {string|number} payRangeId
 * @param {string|number} versionId
 * @param {{
 *   effective_from?: string|null,
 *   effective_to?: string|null,
 *   min_amount?: number|null,
 *   max_amount?: number|null,
 * }} [body]
 */
export async function correctPayRangeVersion(payRangeId, versionId, body = {}) {
  const accountId = getAccountId();
  return axios()({
    method: "post",
    url: `${accountPayRangeBase(accountId)}${payRangeId}/versions/${versionId}/correct/`,
    data: body,
  });
}

function payRangeVersionAction(payRangeId, versionId, action, body) {
  const accountId = getAccountId();
  return axios()({
    method: "post",
    url: `${accountPayRangeBase(accountId)}${payRangeId}/versions/${versionId}/${action}/`,
    ...(body ? { data: body } : {}),
  });
}

/**
 * Create an open-ended Draft version on a PUBLISHED pay range (ER-12159).
 * POST .../pay-range/<pk>/versions/create/
 * @param {string|number} payRangeId
 * @param {{
 *   min_amount: number|string,
 *   max_amount: number|string,
 *   effective_from: string,
 *   sources?: string|null,
 * }} body No `effective_to`: the Draft is open-ended until a later version supersedes it.
 */
export async function createPayRangeVersion(payRangeId, body) {
  const accountId = getAccountId();
  return axios()({
    method: "post",
    url: `${accountPayRangeBase(accountId)}${payRangeId}/versions/create/`,
    data: body,
  });
}

/**
 * Edit a Draft version (every field optional).
 * POST .../pay-range/<pk>/versions/<version_id>/edit/
 */
export async function editPayRangeVersion(payRangeId, versionId, body = {}) {
  return payRangeVersionAction(payRangeId, versionId, "edit", body);
}

/**
 * Discard a Draft version.
 * POST .../pay-range/<pk>/versions/<version_id>/discard/
 */
export async function discardPayRangeVersion(payRangeId, versionId) {
  return payRangeVersionAction(payRangeId, versionId, "discard");
}

/**
 * Publish a Draft version (a Correct-origin Draft voids the corrected version).
 * POST .../pay-range/<pk>/versions/<version_id>/publish/
 */
export async function publishPayRangeVersion(payRangeId, versionId) {
  return payRangeVersionAction(payRangeId, versionId, "publish");
}
